import { useEffect, useRef } from "react";
import { Renderer, Stave, StaveNote, Voice, Formatter, Accidental, Dot, Beam } from "vexflow";

// Accidentals implied by each key signature (major + the minor keys used in the
// curriculum). A note whose accidental matches the key signature must NOT print
// a redundant sign; a natural note against a flat/sharp key needs a natural sign.
const KEY_ACCIDENTALS = {
  C: {}, G: { f: "#" }, D: { f: "#", c: "#" }, A: { f: "#", c: "#", g: "#" },
  E: { f: "#", c: "#", g: "#", d: "#" }, B: { f: "#", c: "#", g: "#", d: "#", a: "#" },
  "F#": { f: "#", c: "#", g: "#", d: "#", a: "#", e: "#" },
  F: { b: "b" }, Bb: { b: "b", e: "b" }, Eb: { b: "b", e: "b", a: "b" },
  Ab: { b: "b", e: "b", a: "b", d: "b" }, Db: { b: "b", e: "b", a: "b", d: "b", g: "b" },
  Am: {}, Em: { f: "#" }, Bm: { f: "#", c: "#" }, "F#m": { f: "#", c: "#", g: "#" },
  Dm: { b: "b" }, Gm: { b: "b", e: "b" }, Cm: { b: "b", e: "b", a: "b" }, Fm: { b: "b", e: "b", a: "b", d: "b" },
};

export const StaveView = ({ notation, width = 340, height = 130, testid = "stave-view", onNoteClick }) => {
  const ref = useRef(null);
  const parts = notation?.parts || null;

  // Optional tap-any-note support: delegated click with forgiving hit targets.
  useEffect(() => {
    const el = ref.current;
    if (!el || !onNoteClick) return undefined;
    const onTap = (e) => {
      const nodes = [...el.querySelectorAll(".vf-stavenote")];
      let best = -1;
      let bestD = Infinity;
      nodes.forEach((nd, i) => {
        const r = nd.getBoundingClientRect();
        const d = Math.hypot(e.clientX - (r.left + r.width / 2), (e.clientY - (r.top + r.height / 2)) * 1.6);
        if (d < bestD) { bestD = d; best = i; }
      });
      if (best >= 0 && bestD < 90) onNoteClick(best);
    };
    el.addEventListener("click", onTap);
    return () => el.removeEventListener("click", onTap);
  }, [onNoteClick]);
  const partH = 118;
  const totalH = parts ? 34 + partH * parts.length : height;

  useEffect(() => {
    if (!ref.current || !notation) return;
    ref.current.replaceChildren();
    try {
      const renderer = new Renderer(ref.current, Renderer.Backends.SVG);
      renderer.resize(width, totalH);
      const ctx = renderer.getContext();
      ctx.setFont("Arial", 10);

      const partList = parts || [{ label: null, notes: notation.notes, clef: notation.clef }];
      partList.forEach((part, pi) => {
        const y = 20 + pi * partH;
        const stave = new Stave(10, y, width - 24);
        stave.addClef(part.clef || notation.clef || "treble");
        if (notation.key_signature) stave.addKeySignature(notation.key_signature);
        if (notation.time_signature) stave.addTimeSignature(notation.time_signature);
        stave.setContext(ctx).draw();
        if (part.label) ctx.fillText(part.label, 10, y - 6);

        const notes = (part.notes || []).map((n) => {
          const sn = new StaveNote({
            clef: part.clef || notation.clef || "treble",
            keys: n.keys,
            duration: n.dur,
          });
          if (n.dot) Dot.buildAndAttach([sn], { all: true });
          const keyAcc = KEY_ACCIDENTALS[notation.key_signature] || {};
          n.keys.forEach((k, i) => {
            const name = k.split("/")[0];
            const letter = name[0];
            const acc = name.slice(1); // accidentals only — "b/4" is B natural, "bb/4" is B-flat
            if (acc === (keyAcc[letter] || "")) return; // already implied by the key signature
            if (acc.startsWith("#") || acc.startsWith("b")) sn.addModifier(new Accidental(acc[0]), i);
            else if (keyAcc[letter]) sn.addModifier(new Accidental("n"), i); // natural against the key
          });
          return sn;
        });
        if (notes.length) {
          const voice = new Voice({ num_beats: 16, beat_value: 4 }).setStrict(false);
          voice.addTickables(notes);
          new Formatter().joinVoices([voice]).format([voice], width - 130);
          voice.draw(ctx, stave);
          // Beaming: group consecutive quavers by beam_groups (e.g. [3, 3] in 6/8 —
          // the beam structure must match the metre, never six loose quavers).
          const groups = part.beam_groups || notation.beam_groups;
          if (groups) {
            const quaverIdx = [];
            (part.notes || []).forEach((n, i) => { if (n.dur === "8") quaverIdx.push(i); });
            let q = 0;
            for (const size of groups) {
              const slice = quaverIdx.slice(q, q + size).map((i) => notes[i]);
              if (slice.length === size && slice.length > 1) new Beam(slice).setContext(ctx).draw();
              q += size;
            }
          }
        }
      });
    } catch (e) {
      console.error("[ChoirQuest notation] render failed:", e);
      if (ref.current) ref.current.replaceChildren();
    }
  }, [notation, width, height, totalH, parts]);

  if (!notation) return null;
  return (
    <div className="stave-card p-3 overflow-x-auto" data-testid={testid}>
      <div ref={ref} className="min-w-fit" />
    </div>
  );
};
